import { useState } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { numOrDefault } from '@/lib/invoiceHelpers';

export default function RegeneratePdfButton({ settlement, onDone }) {
  const [loading, setLoading] = useState(false);

  const handleRegenerate = async () => {
    setLoading(true);
    toast.info('Regenerating PDF...');

    const s = settlement;
    const today = new Date().toISOString().split('T')[0];

    try {
      // Fetch supporting data in parallel
      const [profiles, loads, lineItemsArr, companySettings] = await Promise.all([
        client.entities.DriverProfile.filter({ user_email: s.driver_email }).catch(() => []),
        client.entities.Load.filter({ id: s.load_id }).catch(() => []),
        client.entities.SettlementLineItem.filter({ settlement_id: s.id }).catch(() => []),
        client.entities.CompanySettings.list().catch(() => []),
      ]);
      // Was hardcoded "Carrier Portal" regardless of the carrier's actual
      // configured company name.
      const companyName = companySettings.find(x => x.key === 'company_name')?.value || 'Carrier Portal';

      // Company Name is an Owner Operator's own business entity --
      // checked against driver_type here too (not just truthiness) so a
      // driver switched to Company Driver never shows a stale "Pay To"
      // line naming their old company, just because that field hasn't
      // been cleared on their profile yet.
      const driverCompanyName = (profiles[0]?.driver_type !== 'Company Driver' && profiles[0]?.company_name) || '';
      const loadData = loads[0] || null;

      const fmt = (n) => '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const fmtNeg = (n) => '-' + fmt(n);
      const payToLine = driverCompanyName ? `${driverCompanyName} (${s.driver_name})` : s.driver_name;
      const fromCity = loadData?.origin || '';
      const toCity = loadData?.destination || '';
      const routeStr = fromCity && toCity ? `${fromCity} to ${toCity}` : (fromCity || toCity || '');

      const gross = s.gross_revenue || 0;
      const carrierFee = numOrDefault(s.carrier_take_amount, gross * 0.15);
      const dispatchFee = s.dispatch_fee_amount || 0;
      const fuelAdvance = s.fuel_advance_total || 0;
      const lumperFee = s.lumper_fee_deducted || 0;
      const lateFee = s.late_fee_deducted || 0;
      const netPay = s.final_net_pay ?? s.net_driver_pay ?? 0;

      const deductionItems = lineItemsArr.filter(i => i.type === 'deduction');
      const earningItems = lineItemsArr.filter(i => i.type === 'earning');

      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ format: 'letter', unit: 'pt' });
      const pageW = pdf.internal.pageSize.getWidth();
      const marginL = 48;
      const marginR = pageW - 48;
      const labelCol = 48;
      const valueCol = 200;
      let y = 48;

      const drawLine = (y1) => { pdf.setDrawColor(180, 180, 180); pdf.line(marginL, y1, marginR, y1); };
      const txt = (str, x, yt, opts) => { pdf.text(String(str), x, yt, opts || {}); };
      const row = (label, value, yt, color) => {
        pdf.setTextColor(color || '#111111');
        txt(label, marginL, yt);
        txt(value, marginR, yt, { align: 'right' });
        pdf.setTextColor('#111111');
      };

      // Title
      pdf.setFontSize(22); pdf.setFont(undefined, 'bold');
      txt('Driver Settlement', marginL, y); y += 30;

      // Header info
      pdf.setFontSize(10);
      const headerRows = [
        ['Company:', companyName],
        ['Pay To:', payToLine],
        ['Driver:', s.driver_name],
        ['Email:', s.driver_email],
        ['Date:', s.finalized_date || today],
        ...(s.payment_reference ? [['Ref #:', s.payment_reference]] : []),
        ...(routeStr ? [['Route:', routeStr]] : []),
        ['Load #:', s.load_number || ''],
        ['Settlement Date:', s.settlement_date || s.finalized_date || today],
      ];
      for (const [label, val] of headerRows) {
        pdf.setFont(undefined, 'bold'); txt(label, labelCol, y);
        pdf.setFont(undefined, 'normal'); txt(String(val), valueCol, y);
        y += 17;
      }

      y += 10; drawLine(y); y += 20;

      // Settlement Summary
      pdf.setFontSize(13); pdf.setFont(undefined, 'bold');
      txt('Settlement Summary', marginL, y); y += 8;
      drawLine(y); y += 18;

      pdf.setFontSize(10); pdf.setFont(undefined, 'normal');
      row('Gross Earnings', fmt(gross), y); y += 20;
      row(`Carrier Fee (${numOrDefault(s.carrier_take_percent, 15)}%)`, fmtNeg(carrierFee), y, '#cc0000'); y += 20;
      if (dispatchFee > 0) { row(`Dispatch Fee (${numOrDefault(s.dispatch_fee_percent, 3)}%)`, fmtNeg(dispatchFee), y, '#cc0000'); y += 20; }
      if (fuelAdvance > 0) { row('Fuel Advances', fmtNeg(fuelAdvance), y, '#cc0000'); y += 20; }
      if (lumperFee > 0) { row('Lumper Fee', fmtNeg(lumperFee), y, '#cc0000'); y += 20; }
      if (lateFee > 0) { row('Late Fee', fmtNeg(lateFee), y, '#cc0000'); y += 20; }
      for (const item of deductionItems) {
        row(`Deduction: ${item.memo} (${item.category})`, fmtNeg(Math.abs(item.amount || 0)), y, '#cc0000'); y += 20;
      }
      for (const item of earningItems) {
        row(`Addition: ${item.memo} (${item.category})`, '+' + fmt(Math.abs(item.amount || 0)), y, '#007700'); y += 20;
      }

      y += 6; drawLine(y); y += 18;
      pdf.setFontSize(12); pdf.setFont(undefined, 'bold');
      pdf.setTextColor('#007700');
      row('NET PAY TO DRIVER', fmt(netPay), y);
      pdf.setTextColor('#111111'); y += 24;

      pdf.setFontSize(8); pdf.setFont(undefined, 'italic'); pdf.setTextColor('#888888');
      txt('* Insurance & ELD are weekly fixed costs deducted at pay period start, not included per-load.', marginL, y);
      pdf.setTextColor('#111111'); y += 20;

      drawLine(y); y += 14;
      pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
      txt(`Regenerated by ${companyName} on ${today}`, marginL, y);

      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], `settlement-${s.load_number}-${s.finalized_date || today}.pdf`, { type: 'application/pdf' });
      const { file_url } = await client.integrations.Core.UploadFile({ file: pdfFile });

      await client.entities.Settlement.update(s.id, { pdf_url: file_url });
      toast.success('PDF generated! Opening...');
      window.open(file_url, '_blank');
      if (onDone) onDone();
    } catch (err) {
      console.error('PDF error:', err);
      toast.error('PDF generation failed: ' + err.message);
    }

    setLoading(false);
  };

  return (
    <button
      onClick={handleRegenerate}
      disabled={loading}
      className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-lg text-xs font-semibold hover:bg-amber-500/20 transition-colors disabled:opacity-50"
    >
      <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
      {loading ? 'Generating...' : 'Gen PDF'}
    </button>
  );
}